import styled, {css} from 'styled-components';
import {match} from 'ts-pattern';

import {InactiveSourcingAd} from '../InactiveSourcingAd/InactiveSourcingAd';
import {SOURCING_CARD_GEOMETRY} from './constants/SOURCING_CARD_GEOMETRY';

/**
 * Thickness of the card border, which turns blue for the last clicked row. The 2px is deliberately
 * off the 4px spacing grid (half a step), which the step-based `getSize` cannot express.
 */
const CAR_BORDER_WIDTH = '2px';

/**
 * Image column of the `Car`, see `Car` for the layout. It carries the inactive treatment of the ad
 * and, as a stretched flex item, gives the image a definite height to fill while it sits beside the content.
 */
export const CarImageColumn = styled(InactiveSourcingAd)`
  flex: 1 1 ${({theme}) => theme.getSize(SOURCING_CARD_GEOMETRY.imageColumnWidth)};
`;

/** Content column of the `Car`, see `Car` for the layout. */
export const CarContentColumn = styled.div`
  flex: ${SOURCING_CARD_GEOMETRY.contentGrow} 1
    ${({theme}) => theme.getSize(SOURCING_CARD_GEOMETRY.contentMinWidth)};
  min-width: 0;
`;

type CarProps = {
  $enhancedSurfaces: boolean;
  $isDetail?: boolean;
  $isHoverDisabled?: boolean;
  $isActive?: boolean;
  $isNew?: boolean;
  $isInactive?: boolean;
};

/**
 * Sourcing list card. The image and the content are flex items: when the row is too narrow for both,
 * the content wraps under the image (same as the old mobile column layout). The layout is driven by
 * the card's own width instead of a media query, so it also adapts inside a narrow panel.
 */
export const Car = styled.div<CarProps>`
  display: flex;
  flex-wrap: wrap;
  border-radius: ${({$enhancedSurfaces, $isDetail, theme}) =>
    $enhancedSurfaces && $isDetail
      ? `${theme.radii.small} ${theme.radii.small} 0 0`
      : theme.radii.small};
  ${({theme, $enhancedSurfaces}) =>
    $enhancedSurfaces &&
    css`
      background-color: ${theme.colors.general.white};
      box-shadow: ${theme.shadows.elevation_1};
    `}
  border: ${CAR_BORDER_WIDTH} solid
    ${({$isActive, theme}) => ($isActive ? theme.colors.palettes.blue[60][100] : 'transparent')};

  ${({$enhancedSurfaces, $isDetail, $isHoverDisabled}) =>
    $enhancedSurfaces &&
    !$isDetail &&
    !$isHoverDisabled &&
    css<CarProps>`
      position: relative;
      transition:
        transform 180ms ease,
        border-color 150ms ease,
        box-shadow 150ms ease;
      @media (hover: hover) {
        &:hover {
          border-color: ${({$isActive, theme}) =>
            $isActive ? theme.colors.palettes.blue[60][100] : theme.colors.palettes.blue[40][100]};
          box-shadow: ${({theme}) => theme.shadows.elevation_4};
          transform: translateY(${({theme}) => `calc(-1 * ${theme.getSize(1)})`});
          z-index: 1;
        }
        &:hover::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          /* Cover the resting bottom edge only after the card has lifted. */
          bottom: ${({theme}) => `calc(-1 * ${theme.getSize(1)} - ${CAR_BORDER_WIDTH})`};
          height: ${({theme}) => `calc(${theme.getSize(1)} + ${CAR_BORDER_WIDTH})`};
        }
      }
    `}

  ${({$enhancedSurfaces}) =>
    $enhancedSurfaces &&
    css`
      @media (prefers-reduced-motion: reduce) {
        transition: none;
        &:hover {
          transform: none;
        }
        &:hover::after {
          content: none;
        }
      }
    `}

  ${({$isNew, $isInactive, theme}) =>
    match({$isNew, $isInactive})
      .with({$isNew: true}, () => `background-color: ${theme.colors.palettes.green[20][100]};`)
      .with(
        {$isInactive: true},
        () => `background-color: ${theme.colors.palettes.neutral[30][100]};`
      )
      .otherwise(() => '')}
`;
